/* Imports */
import { createStore } from 'vuex';

// Modules
import customerGroups from './customerGroups.js';
import drawer from './drawer.js';
import map from './map.js';
import pricing from './pricing.js';
import search from './search.js';

export const storeSettings = {
  modules: {
    customerGroups,
    drawer,
    map,
    search,
    pricing,
  },
  state: () => ({
    locale: 'en-US',
    translations: {},
    brands: ['garmin', 'another-brand'],
    pricingProxyServicesBaseUrl: '',
    activeBrand: 'garmin',
    showInstructions: true,
    showInterstitialOverlay: true,
    loading: false,
    chartType: 'nav',
    depthUnit: '',
    seabedAreasState: 'hide',
    safetyDepth: '',
    subdirectory: '',
    showGarminMaps: true,
    showMapSettings: true,
    isMapSettingsOpen: false,
    marineMapsTokenApiKey: null,
    marineMapsTokenApiUrl: null,
    navionicsWebApiServiceBase: '',
    navionicsWebApiTokens: {},
    graphQLEndpoint: '',
    contentfulEnvSettings: {},
    useGraphQL: true,
    isWorldwideUpdates: true,
    selectedPeriod: '2_years',
  }),
  getters: {
    getLocale: (state) => state.locale,
    getCountryCode: (state) => (state?.locale ? state.locale?.split('-', 2)?.[1] : 'US'),
    getTranslations: (state) => state.translations,
    getBrands: (state) => state.brands,
    getPricingProxyServicesBaseUrl: (state) => state.pricingProxyServicesBaseUrl,
    getActiveBrand: (state) => state.activeBrand,
    getShowGarminMaps: (state, getters) => getters.getActiveBrand === 'garmin',
    getSeoTitle: (state, getters) => getters.getTranslations?.ITFE_MARINE_MAPS_SEO_TITLE || 'Garmin | Marine Maps',
    getShowInstructions: (currentState) => currentState.showInstructions,
    getShowInterstitialOverlay: (currentState) => currentState.showInterstitialOverlay,
    getLoading: (currentState) => currentState.loading,
    getChartType: (currentState) => currentState.chartType,
    getChartDepth: (currentState) => currentState.depthUnit,
    getSeabedAreasState: (currentState) => currentState.seabedAreasState,
    getSafetyDepth: (currentState) => currentState.safetyDepth,
    getShowMapSettings: (currentState) => currentState.showMapSettings,
    getIsMapSettingsOpen: (currentState) => currentState.isMapSettingsOpen,
    getGarminTileDomain: () => {
      const origin = window?.location?.origin || '';
      const validOrigins = [
        'https://localhost.garmin.com:6024',
        'https://dev.maps.garmin.com',
        'https://marine-maps.pre.dev.cv1.garmin.com',
        'https://wwwpub.garmin.com',
        'https://marine-maps.pre.prod.cv1.garmin.com',
        'https://marine-maps.pre.prod.kc3.garmin.com',
        'https://maps.garmin.com',
      ];
      const matchedOrigin = validOrigins.filter((originVal) => originVal === origin);
      // If the current origin doesn't match a valid origin, return undefined and the call that uses this will fail safely
      return matchedOrigin.length > 0 ? origin : undefined;
    },
    getMarineMapsTokenApiKey: (currentState) => currentState.marineMapsTokenApiKey,
    getMarineMapsTokenApiUrl: (currentState) => currentState.marineMapsTokenApiUrl,
    getNavionicsWebApiServiceBase: (currentState) => currentState.navionicsWebApiServiceBase,
    getNavionicsWebApiTokens: (currentState) => currentState.navionicsWebApiTokens,
    getSubdirectory: (currentState) => currentState.subdirectory,
    getGraphQLEndpoint: (currentState) => currentState.graphQLEndpoint,
    getContentfulEnvSettings: (currentState) => currentState.contentfulEnvSettings,
    getEnableGNVPro: (state, getters) => getters.getContentfulEnvSettings.ITFE_MARINE_MAPS_ENABLE_GNV_PRO || false,
    getUseGraphQL: (currentState) => currentState.useGraphQL,
    getIsWorldwideUpdates: (currentState) => currentState.isWorldwideUpdates,
    getSelectedPeriod: (currentState) => currentState.selectedPeriod,
  },
  mutations: {
    setLocale(state, locale) {
      state.locale = locale;
    },
    setTranslations(state, translations) {
      state.translations = translations;
    },
    setActiveBrand(state, activeBrand) {
      state.activeBrand = activeBrand;
    },
    setPricingProxyServicesBaseUrl: (state, value) => {
      state.pricingProxyServicesBaseUrl = value;
    },
    setShowGarminMaps(state, showGarminMaps) {
      state.showGarminMaps = showGarminMaps;
    },
    setShowInstructions(state, showInstructions) {
      state.showInstructions = showInstructions;
    },
    setShowInterstitialOverlay(state, showInterstitialOverlay) {
      state.showInterstitialOverlay = showInterstitialOverlay;
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setChartType(state, payload) {
      state.chartType = payload;
    },
    setChartDepth(state, payload) {
      state.depthUnit = payload;
    },
    setSafetyDepth(state, payload) {
      state.safetyDepth = payload;
    },
    setSeabedAreasState(state, payload) {
      state.seabedAreasState = payload;
    },
    setSubdirectory(state, payload) {
      state.subdirectory = payload;
    },
    setShowMapSettings(state, payload) {
      state.showMapSettings = payload;
    },
    setIsMapSettingsOpen(state, payload) {
      state.isMapSettingsOpen = payload;
    },
    setMarineMapsTokenApiKey(state, payload) {
      state.marineMapsTokenApiKey = payload;
    },
    setMarineMapsTokenApiUrl(state, payload) {
      state.marineMapsTokenApiUrl = payload;
    },
    setNavionicsWebApiServiceBase(state, payload) {
      state.navionicsWebApiServiceBase = payload;
    },
    setNavionicsWebApiTokens(state, payload) {
      state.navionicsWebApiTokens = payload;
    },
    setGraphQLEndpoint(state, payload) {
      state.graphQLEndpoint = payload;
    },
    setContentfulEnvSettings(state, payload) {
      state.contentfulEnvSettings = payload;
    },
    setUseGraphQL(state, payload) {
      state.useGraphQL = payload;
    },
    setIsWorldwideUpdates(state, payload) {
      state.isWorldwideUpdates = payload;
    },
    setSelectedPeriod(state, payload) {
      state.selectedPeriod = payload;
    },
  },
  actions: {
    setLocale({ commit }, payload) {
      commit('setLocale', payload);
    },
    setTranslations({ commit }, payload) {
      commit('setTranslations', payload);
    },
    setShowGarminMaps({ commit }, payload) {
      commit('setShowGarminMaps', payload);
    },
    async setShowInstructions({ commit }, payload) {
      commit('setShowInstructions', payload);
    },
    async setLoading({ commit }, payload) {
      commit('setLoading', payload);
    },
    setChartType({ commit }, payload) {
      commit('setChartType', payload);
    },
    setChartDepth({ commit }, payload) {
      commit('setChartDepth', payload);
    },
    setSafetyDepth({ commit }, payload) {
      commit('setSafetyDepth', payload);
    },
    setSeabedAreasState({ commit }, payload) {
      commit('setSeabedAreasState', payload);
    },
    setShowMapSettings({ commit }, payload) {
      commit('setShowMapSettings', payload);
    },
    setIsMapSettingsOpen({ commit }, payload) {
      commit('setIsMapSettingsOpen', payload);
    },
    setSelectedPeriod({ commit }, payload) {
      commit('setSelectedPeriod', payload);
    },
    setShowInterstitialOverlay({ commit }, payload) {
      commit('setShowInterstitialOverlay', payload);
    },
    setActiveBrand({ commit, getters }, payload) {
      const { getBrands } = getters;
      const {
        mapsQueryParamBrandValue,
        overlayQueryParamValue,
      } = payload;

      const validOverlayValues = ['true', 'false'];
      const hasValidOverlayValue = (overlayVal) => validOverlayValues.includes(overlayVal);
      const hasValidBrand = (brand) => getBrands.includes(brand);

      if (overlayQueryParamValue === 'true') {
        commit('setShowInterstitialOverlay', true);
      }

      // If overlay or maps query param are not valid, show user interstitial overlay to have them reselect a brand
      if (!hasValidOverlayValue(overlayQueryParamValue) || !hasValidBrand(mapsQueryParamBrandValue)) {
        commit('setShowInterstitialOverlay', true);
      }

      if (overlayQueryParamValue === 'false') {
        // Only show map viewer if maps query param has a valid brand value
        if (hasValidBrand(mapsQueryParamBrandValue)) {
          commit('setActiveBrand', mapsQueryParamBrandValue);
          commit('setShowInterstitialOverlay', false);
        } else {
        // Otherwise, show the user the interstitial to make them select a brand
          commit('setShowInterstitialOverlay', true);
        }
      }
    },
    async fetchNavionicsWebApiTokens({ commit }) {
      const navionicsTokensUrl = '/marine/api/getNavionicsTokens';

      try {
        const response = await fetch(navionicsTokensUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Received ${response.status}: ${response.statusText}`);
        }
        const navionicsWebApiTokens = await response.json();

        commit('setNavionicsWebApiTokens', navionicsWebApiTokens);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Unable to fetch navionics tokens: ${error}`);
      }
    },
    async setInitialState({ commit, dispatch }) {
      const {
        pricingProxyServicesBaseUrl = '',
        locale = 'en-US',
        translations = {},
        subdirectory,
        marineMapsTokenApiKey = '',
        marineMapsTokenApiUrl = '',
        navionicsWebApiServiceBase,
        graphQLEndpoint = '',
        contentfulEnvSettings = {},
        useGraphQL = true,
      } = window.AppData;

      // Get initial URL query params
      const initialSearchParams = new URLSearchParams(window?.location?.search);
      const mapsQueryParamBrandValue = initialSearchParams.get('maps');
      const overlayQueryParamValue = initialSearchParams.get('overlay');

      dispatch('setActiveBrand', {
        mapsQueryParamBrandValue,
        overlayQueryParamValue,
      });

      commit('setTranslations', translations);
      // Need to wait for this tokens before we load navionics tiles
      await dispatch('fetchNavionicsWebApiTokens');
      dispatch('setInitialMapLocation');
      commit('setPricingProxyServicesBaseUrl', pricingProxyServicesBaseUrl);
      commit('setLocale', locale);
      commit('setSubdirectory', subdirectory);
      commit('setMarineMapsTokenApiKey', marineMapsTokenApiKey);
      commit('setMarineMapsTokenApiUrl', marineMapsTokenApiUrl);
      commit('setNavionicsWebApiServiceBase', navionicsWebApiServiceBase);
      commit('setGraphQLEndpoint', graphQLEndpoint);
      commit('setContentfulEnvSettings', contentfulEnvSettings);
      commit('setUseGraphQL', useGraphQL);
      const chartDepth = locale === 'en-US' ? 'ft' : 'm';
      const safetyDepth = locale === 'en-US' ? '6' : '2';

      commit('setChartDepth', chartDepth);
      commit('setSafetyDepth', safetyDepth);
    },
    setIsWorldwideUpdates({ commit }, payload) {
      commit('setIsWorldwideUpdates', payload);
    },
  },
};

export default createStore(storeSettings);
